import { format } from 'date-fns';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { useRetrieveOrderSaleQuery } from '@sophocles/features/entities/orderSales';
import { productSlice } from '@sophocles/features/warehouse/products/slice';
import { BrandLogo } from '@sophocles/theme';

import '@sophocles/styles/print-order.css';

function PrintableOrderView() {
  const { orderId } = useParams();
  const { t } = useTranslation();

  const { data: order, isLoading: loading } = useRetrieveOrderSaleQuery({ id: orderId }, { skip: orderId == null });
  const products = useSelector(state => productSlice.selectors.selectAllEntities(state));

  if (loading || !order) {
    return (
      <div className="print-order-page">
        <p>{t('warehouse.loadingOrder')}</p>
      </div>
    );
  }

  if (order.status === 'Draft') {
    return (
      <div className="print-order-page">
        <p>{t('warehouse.draftCannotPrint')}</p>
      </div>
    );
  }

  const isShipped = ['Shipped', 'Billing - On Hold', 'Complete'].includes(order.status);
  const items = order.order_sale_items || [];
  const getProduct = itemId => products.find(p => p.id === itemId);
  const colCount = isShipped ? 3 : 2;

  const formatDate = dateStr => {
    if (!dateStr) return null;
    try {
      return format(new Date(dateStr), 'MM/dd/yyyy');
    } catch {
      return dateStr;
    }
  };

  const orderDate = formatDate(order.order_time) || '\u2014';

  const renderAddress = (label, fields) => {
    const { contact, company, address, city, state, zip, country, extra } = fields;
    return (
      <div className="print-order-info-col">
        <h3>{label}</h3>
        {contact && (
          <p>
            <strong>{contact}</strong>
          </p>
        )}
        {company && <p>{company}</p>}
        {address && <p>{address}</p>}
        <p>
          {[city, state].filter(Boolean).join(', ')}
          {zip ? ' ' + zip : ''}
        </p>
        {country && <p>{country}</p>}
        {extra && extra.map((line, i) => <p key={i}>{line}</p>)}
      </div>
    );
  };

  const renderLineItem = (item, index) => {
    const product = getProduct(item.item);
    const productLabel = product
      ? product.item_part_num + ': ' + product.item_name
      : t('warehouse.productNumber', { number: item.item });

    const rows = [];

    rows.push(
      <tr key={'main-' + index}>
        <td>
          <div className="item-name">{productLabel}</div>
          {product?.item_description && <div className="item-description">{product.item_description}</div>}
        </td>
        <td className="text-right">{item.qty_order}</td>
        {isShipped && <td className="text-right">{item.qty_fill || 0}</td>}
      </tr>,
    );

    if (item.has_radios && item.radio_billing_type === 'Prepaid') {
      rows.push(
        <tr key={'prepaid-' + index} className="prepaid-row">
          <td>{t('warehouse.prepaidTerm', { months: item.term_length_prepaid })}</td>
          <td className="text-right">{item.qty_order}</td>
          {isShipped && <td className="text-right"></td>}
        </tr>,
      );
    }

    if (item.has_radios && isShipped && item.iccids?.length > 0) {
      const iccidText = item.iccids
        .filter(i => i.iccid)
        .map(i => i.iccid)
        .join(', ');
      if (iccidText) {
        rows.push(
          <tr key={'iccid-' + index} className="iccid-row">
            <td colSpan={colCount}>{t('warehouse.iccids', { value: iccidText })}</td>
          </tr>,
        );
      }
    }

    return rows;
  };

  const shipExtra = [];
  if (order.ship_instructions) shipExtra.push(t('warehouse.instructions') + ': ' + order.ship_instructions);

  return (
    <div className="print-order-page">
      <div className="no-print">
        <button
          onClick={() => window.print()}
          style={{
            padding: '8px 24px',
            fontSize: '14px',
            cursor: 'pointer',
            backgroundColor: 'var(--brand-primary)',
            color: 'white',
            border: 'none',
            borderRadius: 'var(--radius-md)',
          }}
        >
          {t('actions.print')}
        </button>
      </div>

      <div className="print-order-header">
        <BrandLogo size="large" />
        <div className="print-order-header-right">
          <h1>{t('warehouse.orderConfirmation')}</h1>
        </div>
      </div>

      <div className="print-order-info-row">
        <div className="print-order-info-col">
          <h3>{t('general.customer')}</h3>
          <p>
            <strong>
              {[order.site_group_detail?.name, order.site_group_detail?.description].filter(Boolean).join(' | ') ||
                order.customer_detail?.name ||
                '\u2014'}
            </strong>
          </p>
        </div>
        <div className="print-order-info-col">
          <h3>{t('warehouse.orderDetails')}</h3>
          <p>
            <strong>{t('warehouse.orderNumber')}:</strong> {order.id}
          </p>
          <p>
            <strong>{t('general.status')}:</strong> {order.status}
          </p>
          <p>
            <strong>{t('warehouse.orderDate')}:</strong> {orderDate}
          </p>
          <p>
            <strong>{t('warehouse.shippedDate')}:</strong> {formatDate(order.ship_date) || '\u2014'}
          </p>
          <p>
            <strong>{t('warehouse.requestedBy')}:</strong> {formatDate(order.date_req_by) || '\u2014'}
          </p>
          <p>
            <strong>{t('warehouse.trackingNumber')}:</strong> {order.tracking || '\u2014'}
          </p>
          <p>
            <strong>{t('warehouse.poNumber')}:</strong> {order.po_num || '\u2014'}
          </p>
        </div>
      </div>

      <div className="print-order-info-row">
        {renderAddress(t('warehouse.shipTo'), {
          contact: order.ship_contact,
          company: order.ship_company_name,
          address: order.ship_address,
          city: order.ship_city,
          state: order.ship_state,
          zip: order.ship_zip,
          country: order.ship_country,
          extra: shipExtra.length > 0 ? shipExtra : null,
        })}
        {renderAddress(t('warehouse.billTo'), {
          contact: order.bill_contact,
          company: order.bill_company_name,
          address: order.bill_address,
          city: order.bill_city,
          state: order.bill_state,
          zip: order.bill_zip,
          country: order.bill_country,
        })}
      </div>

      <table className="print-order-table">
        <thead>
          <tr>
            <th>{t('general.item')}</th>
            <th className="text-right">{t('warehouse.qtyOrdered')}</th>
            {isShipped && <th className="text-right">{t('warehouse.qtyShipped')}</th>}
          </tr>
        </thead>
        <tbody>{items.map((item, index) => renderLineItem(item, index))}</tbody>
      </table>

      {order.comments && (
        <div className="print-order-comments">
          <h3>{t('general.comments')}</h3>
          <p>{order.comments}</p>
        </div>
      )}

      <div className="print-order-footer">
        <p>{t('warehouse.thankYou')}</p>
      </div>
    </div>
  );
}

export default PrintableOrderView;
